// src/components/dfs/MobileLineupDisplay.tsx
// FINAL CORRECTED VERSION: Removes all unused variables and imports.

import React from 'react';
// --- FIX 1: Removed unused 'SubmittedLineupResponse' import ---
import { SubmittedLineupPlayerDetail } from '@/api/dfs';
import { ApiPlayer } from '@/api/dfs';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { AnimatedScore } from '@/components/common/AnimatedNumericDisplay';
import { formatMatchTime, formatMatchDateTime, getOprkColorClass, OPRK_TOOLTIP } from '@/utils/playerDisplay';
import { useNow } from '@/hooks/useNow';
import { formatMatchupAbbreviation } from '@/utils/teams';
import { parseISO, isPast } from 'date-fns';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { useScoreChangeHighlight } from '@/hooks/useScoreChangeHighlight';

interface MobilePlayerRowProps {
  playerDetail: SubmittedLineupPlayerDetail;
  previousScore?: number;
  onPlayerClick: (player: ApiPlayer) => void;
  onScoreClick: (player: ApiPlayer) => void;
  onMatchInfoClick: (player: ApiPlayer) => void;
}

const MobilePlayerRow: React.FC<MobilePlayerRowProps> = ({ playerDetail, previousScore, onPlayerClick, onScoreClick, onMatchInfoClick }) => {
    const player = playerDetail.player;
    const score = player?.current_score ?? 0.0;
    const matchHasStarted = player?.match_time ? isPast(parseISO(player.match_time)) : false;
    const canViewBreakdown = matchHasStarted && score !== 0.0;
    const { isHighlight: isScoreHighlight } = useScoreChangeHighlight(score);
    const now = useNow();

    if (!player) return null;

    // ESPN drives the matchup display. Abbrs present = ESPN has this match.
    // match_clock null = pre; FT/AET/PEN = final; else live.
    const hasEspnMatchData = !!(player.home_team_abbr && player.away_team_abbr);
    const isPreMatch = hasEspnMatchData && player.match_clock == null;
    const isFinalMatch = hasEspnMatchData && !!player.match_clock && ['FT','AET','PEN'].includes(player.match_clock);

    return (
        <div
            className={`group relative hover:bg-primary/80 active:bg-primary/80 cursor-pointer border-b border-gray-700/30 last:border-b-0 transition-colors duration-[1500ms] ${isScoreHighlight ? 'bg-green-500/15' : 'bg-primary/30'}`}
            onClick={() => onPlayerClick(player)}
        >
            {/* Two-zone row, identical anatomy to the desktop CurrentLineup
                card (owner call: that layout IS the mobile layout): identity
                (name over FPPG · OPRK · salary) grows, matchup + clock stack
                right-of-center, score stays rightmost. */}
            <div className="px-2 py-2 flex items-center gap-2">
                <span className="w-8 text-center text-xs font-bold text-accent-yellow flex-shrink-0">
                    {playerDetail.assigned_position}
                </span>

                <div className="flex-grow flex justify-between items-center min-w-0 gap-2">
                    {/* Name gets the full identity width. Availability belongs
                        to the fixture stack so status never truncates identity. */}
                    <div className="flex flex-col min-w-0">
                        <div className="min-w-0">
                            <p className="break-words font-semibold text-sm text-gray-100 group-hover:text-accent-yellow group-hover:underline group-hover:underline-offset-2 group-active:text-accent-yellow group-active:underline group-active:underline-offset-2">
                                {formatPlayerDisplayName(player)}
                            </p>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-badge text-gray-400">
                            <span>
                                <span className="text-accent-light-blue/90 font-medium">FPPG</span>
                                <span className="text-gray-100 ml-0.5">{player.fppg?.toFixed(1) ?? '-'}</span>
                            </span>
                            <span className={getOprkColorClass(player.oprk)}>
                                <span className="text-accent-light-blue/90 font-medium" title={OPRK_TOOLTIP}>OPRK</span>
                                <span className="ml-0.5">{player.oprk?.toFixed(0) ?? '-'}</span>
                            </span>
                            <span className="text-accent-yellow font-semibold">
                                ${(player.dfs_salary ?? 0).toLocaleString()}
                            </span>
                            <InjuryBadge status={player.injury_status} details={player.injury_details} />
                        </div>
                    </div>

                    {/* Availability + matchup + time/clock block */}
                    <div className="flex flex-col items-center flex-shrink-0">
                        <div className="flex min-h-[18px] items-center justify-center">
                            <AvailabilityBadge status={player.availability_status} isLocked={matchHasStarted} />
                        </div>
                        <span
                            className="text-badge font-medium text-accent-light-blue/80 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-accent-light-blue active:text-accent-light-blue transition-colors"
                            onClick={(e) => { e.stopPropagation(); onMatchInfoClick(player); }}
                            role="button"
                            tabIndex={0}
                            title="View match details"
                        >
                            {hasEspnMatchData
                                ? (isPreMatch
                                    ? `${player.home_team_abbr} v ${player.away_team_abbr}`
                                    : `${player.home_team_abbr} ${player.home_score ?? 0}-${player.away_score ?? 0} ${player.away_team_abbr}`)
                                : formatMatchupAbbreviation(player.current_team_name, player.opponent)}
                        </span>
                        {hasEspnMatchData && !isPreMatch ? (
                            <span className={`text-badge mt-0.5 font-semibold ${isFinalMatch ? 'text-gray-500' : 'text-green-400'}`}>
                                {player.match_clock}
                            </span>
                        ) : player.match_time ? (
                            <span className="text-badge mt-0.5 text-gray-400">{formatMatchDateTime(player.match_time, now) ?? formatMatchTime(player.match_time)}</span>
                        ) : null}
                    </div>
                </div>

                {/* Score */}
                <div
                    className={`text-right flex-shrink-0 ${canViewBreakdown ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                        if (canViewBreakdown) {
                            e.stopPropagation();
                            onScoreClick(player);
                        }
                    }}
                    title={canViewBreakdown ? 'View Score Breakdown' : 'Fantasy Score'}
                >
                    <div className={`flex items-center justify-end ${canViewBreakdown ? 'underline decoration-dotted underline-offset-2' : ''}`}>
                        <AnimatedScore score={score} previousScore={previousScore} />
                    </div>
                </div>
            </div>
        </div>
    );
};


interface MobileLineupDisplayProps {
    sortedPlayers: SubmittedLineupPlayerDetail[];
    previousPlayerScores?: Record<string, number>;
    onPlayerClick: (player: ApiPlayer) => void;
    onScoreClick: (player: ApiPlayer) => void;
    onMatchInfoClick: (player: ApiPlayer) => void;
}

export const MobileLineupDisplay: React.FC<MobileLineupDisplayProps> = ({
    sortedPlayers, previousPlayerScores, onPlayerClick, onScoreClick, onMatchInfoClick
}) => {
    return (
        <div className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
            <div className="bg-primary-dark px-4 py-3 border-b border-gray-700">
                <h2 className="font-heading font-bold text-lg text-accent-light-blue">MY LINEUP</h2>
            </div>
            <div>
                {sortedPlayers.map((playerDetail) => (
                    <MobilePlayerRow
                        key={`${playerDetail.player_id}-${playerDetail.assigned_position}`}
                        playerDetail={playerDetail}
                        previousScore={previousPlayerScores?.[playerDetail.player_id]}
                        onPlayerClick={onPlayerClick}
                        onScoreClick={onScoreClick}
                        onMatchInfoClick={onMatchInfoClick}
                    />
                ))}
            </div>
        </div>
    );
};
